import { useState, useRef, useEffect, useCallback } from "react";
import { Mic, MicOff, Play, Pause, Trash2, Loader2, ScrollText, Lock, Pencil, RotateCcw, Sparkles, Globe } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const WINDOW = 60; // one continuous 60-second take over the whole reel

// A self-contained mini voice-over studio for Minute Mode — the same studio graphic + mic + VU
// meter as the DIY studio, but a SINGLE 60s take (no per-scene clips) with an editable teleprompter.
export default function MinuteVoiceStudio({ dataUrl, onRecorded, onClear, defaultScript = "", onScriptChange, onBrandPack }) {
  const [phase, setPhase] = useState("idle"); // idle | recording | saving
  const [remaining, setRemaining] = useState(WINDOW);
  const [level, setLevel] = useState(0);
  const [clip, setClip] = useState(false);
  const [micLive, setMicLive] = useState(false);
  const [muted, setMuted] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [playPct, setPlayPct] = useState(0);
  const [script, setScript] = useState(defaultScript);
  const [locked, setLocked] = useState(!!defaultScript);
  const [ttsBusy, setTtsBusy] = useState(null); // null | 'male' | 'female' — AI voice-over generation
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [useDomain, setUseDomain] = useState(false);
  const [domain, setDomain] = useState("");
  const [countdown, setCountdown] = useState(0); // 3..1 pre-roll before recording

  // Keep the parent (and the saved reel) in sync with the script text.
  useEffect(() => { onScriptChange?.(script); }, [script]); // eslint-disable-line
  // When an existing reel loads its saved script, prefill + lock it in.
  useEffect(() => {
    if (defaultScript && defaultScript !== script) { setScript(defaultScript); setLocked(true); }
  }, [defaultScript]); // eslint-disable-line

  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const tickRef = useRef(null);
  const ctxRef = useRef(null);
  const analyserRef = useRef(null);
  const rafRef = useRef(null);
  const localUrlRef = useRef(null);
  const audioElRef = useRef(null);
  const mutedRef = useRef(false);
  const lastPaintRef = useRef(0);
  const lastAboveRef = useRef(0);
  const liveRef = useRef(false);

  const ac = useCallback(() => {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return null;
    if (!ctxRef.current) ctxRef.current = new AC();
    if (ctxRef.current.state === "suspended") ctxRef.current.resume();
    return ctxRef.current;
  }, []);

  const startMeter = useCallback((stream) => {
    try {
      const ctx = ac(); if (!ctx) return;
      const an = ctx.createAnalyser();
      an.fftSize = 512;
      ctx.createMediaStreamSource(stream).connect(an);
      analyserRef.current = an;
      const buf = new Uint8Array(an.fftSize);
      const loop = () => {
        if (!analyserRef.current) return;
        rafRef.current = requestAnimationFrame(loop);
        analyserRef.current.getByteTimeDomainData(buf);
        let peak = 0;
        for (let i = 0; i < buf.length; i++) { const v = Math.abs(buf[i] - 128) / 128; if (v > peak) peak = v; }
        const now = performance.now();
        // Throttle state updates to ~30fps so we don't re-render the whole studio every frame (mobile jank).
        if (now - lastPaintRef.current < 33) return;
        lastPaintRef.current = now;
        setLevel(peak);
        setClip(peak > 0.92);
        // "Mic live" with hysteresis + a hold, so ambient wobble around the threshold can't flip the
        // status message (and its height) rapidly — that flip was what made the panel jitter.
        if (peak > 0.06) lastAboveRef.current = now;
        const live = now - lastAboveRef.current < 1500;
        if (live !== liveRef.current) { liveRef.current = live; setMicLive(live); }
      };
      loop();
    } catch { /* meter optional */ }
  }, [ac]);

  const ensureMic = useCallback(async () => {
    if (streamRef.current) { try { ac(); } catch { /* noop */ } return streamRef.current; }
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    streamRef.current = stream;
    stream.getAudioTracks().forEach((t) => { t.enabled = !mutedRef.current; });
    startMeter(stream);
    return stream;
  }, [ac, startMeter]);

  useEffect(() => {
    let off = false;
    (async () => { try { if (!off) await ensureMic(); } catch { /* denied */ } })();
    // Browsers start the AudioContext suspended until a user gesture — resume it (and (re)arm the
    // meter) on the first interaction anywhere, so the level actually moves.
    const kick = async () => {
      try { const ctx = ac(); if (ctx && ctx.state === "suspended") await ctx.resume(); } catch { /* noop */ }
      try { const s = await ensureMic(); if (s && !analyserRef.current) startMeter(s); } catch { /* noop */ }
    };
    window.addEventListener("pointerdown", kick);
    window.addEventListener("keydown", kick);
    window.addEventListener("touchstart", kick, { passive: true });
    return () => {
      off = true;
      window.removeEventListener("pointerdown", kick);
      window.removeEventListener("keydown", kick);
      window.removeEventListener("touchstart", kick);
      if (tickRef.current) clearInterval(tickRef.current);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
      if (audioElRef.current) { try { audioElRef.current.pause(); } catch { /* noop */ } }
      if (localUrlRef.current) URL.revokeObjectURL(localUrlRef.current);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleMute = () => setMuted((m) => {
    const next = !m;
    mutedRef.current = next;
    if (streamRef.current) streamRef.current.getAudioTracks().forEach((t) => { t.enabled = !next; });
    if (next) { setLevel(0); setClip(false); setMicLive(false); liveRef.current = false; lastAboveRef.current = 0; }
    return next;
  });

  const stopPreview = () => {
    if (audioElRef.current) { try { audioElRef.current.pause(); } catch { /* noop */ } audioElRef.current = null; }
    setPlaying(false);
    setPlayPct(0);
  };

  const beginRecord = async () => {
    if (phase === "recording" || countdown > 0) return;
    try { const ctx = ac(); if (ctx && ctx.state === "suspended") await ctx.resume(); await ensureMic(); } catch { /* mic prompt handled in startRec */ }
    setCountdown(3);
    await new Promise((r) => {
      const iv = setInterval(() => {
        setCountdown((c) => {
          if (c <= 1) { clearInterval(iv); r(); return 0; }
          return c - 1;
        });
      }, 700);
    });
    startRec();
  };

  const startRec = async () => {
    stopPreview();
    try {
      const stream = await ensureMic();
      stream.getAudioTracks().forEach((t) => { t.enabled = true; });
      if (muted) setMuted(false);
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size) chunksRef.current.push(e.data); };
      mr.onstop = () => {
        if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
        const blob = new Blob(chunksRef.current, { type: mr.mimeType || "audio/webm" });
        if (!blob.size) { setPhase("idle"); return; }
        setPhase("saving");
        const r = new FileReader();
        r.onload = () => {
          if (localUrlRef.current) URL.revokeObjectURL(localUrlRef.current);
          localUrlRef.current = URL.createObjectURL(blob);
          onRecorded?.(r.result);
          setPhase("idle");
          toast.success("60-second take saved 🎙️");
        };
        r.readAsDataURL(blob);
      };
      mediaRef.current = mr;
      mr.start();
      setPhase("recording");
      setRemaining(WINDOW);
      const startedAt = Date.now();
      tickRef.current = setInterval(() => {
        const left = WINDOW - (Date.now() - startedAt) / 1000;
        if (left <= 0) { setRemaining(0); stopRec(); } else setRemaining(left);
      }, 100);
    } catch {
      toast.error("Microphone access denied. Allow mic access to record.");
    }
  };

  const stopRec = () => {
    if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
    if (mediaRef.current && mediaRef.current.state !== "inactive") mediaRef.current.stop();
  };

  const play = () => {
    if (playing) { stopPreview(); return; }
    const url = localUrlRef.current || dataUrl;
    if (!url) return;
    const a = new Audio(url);
    audioElRef.current = a;
    setPlaying(true);
    setPlayPct(0);
    a.ontimeupdate = () => { if (a.duration) setPlayPct((a.currentTime / a.duration) * 100); };
    a.onended = () => { setPlaying(false); setPlayPct(0); };
    a.play().catch(() => setPlaying(false));
  };

  const removeTake = () => { stopPreview(); if (localUrlRef.current) { URL.revokeObjectURL(localUrlRef.current); localUrlRef.current = null; } onClear?.(); };

  const hasTake = !!dataUrl;
  const pct = phase === "recording" ? Math.max(0, (remaining / WINDOW) * 100) : 100;

  const SEG = 16;
  const lit = Math.min(SEG, Math.round(level * SEG * 1.15));
  const segColor = (i) => (i >= 13 ? "#ef4444" : i >= 10 ? "#eab308" : "#22c55e");
  const db = level > 0.0015 ? Math.round(20 * Math.log10(level)) : null;
  const readout = clip ? "CLIP" : db === null ? "−∞" : `${db} dB`;

  const lockScript = () => { if ((script || "").trim()) setLocked(true); };

  const runTTS = async (text, gender) => {
    setTtsBusy(gender);
    try {
      const { data } = await api.post("/manuals/minute/tts", { text, gender });
      if (localUrlRef.current) { try { URL.revokeObjectURL(localUrlRef.current); } catch (e) {} localUrlRef.current = null; }
      onRecorded?.(data.audio);
      toast.success(`${gender === "male" ? "Male" : "Female"} AI voice-over ready 🎧`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't generate that voice-over");
    } finally {
      setTtsBusy(null);
    }
  };

  const genVoice = async (gender) => {
    const t = (script || "").trim();
    if (!t) { toast.error("Add script text first (type or hit Gen), then pick a voice"); return; }
    if (phase === "recording" || countdown > 0 || ttsBusy) return;
    // Estimate spoken length (~2.5 words/sec ≈ 150 wpm). Allow ~10s grace over the 60s window.
    const words = t.split(/\s+/).filter(Boolean);
    const estSec = Math.round(words.length / 2.5);
    if (estSec > 70) {
      const maxWords = Math.round(60 * 2.5); // ~150 words ≈ 60s
      let trimmed = words.slice(0, maxWords).join(" ");
      const lastEnd = Math.max(trimmed.lastIndexOf("."), trimmed.lastIndexOf("!"), trimmed.lastIndexOf("?"));
      if (lastEnd > trimmed.length * 0.5) trimmed = trimmed.slice(0, lastEnd + 1);
      trimmed = trimmed.trim();
      toast.warning(`That script runs about ${estSec}s — longer than the ~60s voice-over limit.`, {
        duration: 9000,
        description: "I can trim it to roughly 60 seconds, or you can shorten it yourself.",
        action: { label: "Trim to ~60s & generate", onClick: () => { setScript(trimmed); runTTS(trimmed, gender); } },
        cancel: { label: "I'll edit it" },
      });
      return;
    }
    runTTS(t, gender);
  };

  const genScript = async () => {
    const topic = aiPrompt.trim();
    const dom = useDomain ? domain.trim() : "";
    if (!topic && !dom) { toast.error(useDomain ? "Enter your website address first" : "Type what your reel is about first"); return; }
    setAiBusy(true);
    try {
      if (dom) {
        toast.message("Reading your website & building your brand…");
        const { data } = await api.post("/minute/brand-pack", { domain: dom, prompt: topic, seconds: 60 });
        setScript(data.script || "");
        setLocked(false);
        onBrandPack?.(data);
        toast.success("Script + brand pulled from your site — tweak it, then Lock in ✍️");
      } else {
        const { data } = await api.post("/minute/script", { prompt: topic, seconds: 60 });
        setScript(data.script || "");
        setLocked(false);
        toast.success("Script drafted — tweak it, then Lock in ✍️");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't write a script — try again");
    } finally {
      setAiBusy(false);
    }
  };

  return (
    <div className="rounded-2xl overflow-hidden bg-[#0b0b0e] border-2 border-[#5d8a52] flex flex-col" data-testid="minute-voice-studio" onPointerDown={() => { try { ac(); } catch { /* noop */ } }}>
      {/* Top console: VU meter + mute */}
      <div className="shrink-0 px-3 pt-3 pb-3 bg-[#0c0c11] border-b border-white/10">
        <div className="flex items-center gap-2">
          <div className="flex-1 h-7 rounded-lg bg-black/70 flex items-center gap-[2px] px-1.5 overflow-hidden"
            style={{ border: (phase === "recording" || clip) ? "1px solid rgba(239,68,68,.9)" : "1px solid rgba(255,255,255,.14)", boxShadow: clip ? "0 0 10px 1px rgba(239,68,68,.6)" : "none" }}
            data-testid="minute-vu-meter" title="Microphone input level">
            {Array.from({ length: SEG }).map((_, i) => {
              const on = i < lit;
              return <span key={i} className="flex-1 h-3.5 rounded-[1px] transition-all duration-75" style={{ background: on ? segColor(i) : "rgba(255,255,255,.07)", boxShadow: on ? `0 0 4px ${segColor(i)}` : "none" }} />;
            })}
          </div>
          <span className="shrink-0 w-12 text-center font-mono font-bold text-[11px] tabular-nums" style={{ color: clip ? "#ef4444" : level > 0.0015 ? "#f0c975" : "rgba(255,255,255,.4)" }}>{readout}</span>
          <button onClick={toggleMute} data-testid="minute-vu-mute" aria-pressed={muted}
            className={`shrink-0 flex items-center gap-1 rounded-lg px-2.5 h-7 text-[11px] font-bold transition-colors ${muted ? "bg-[#ef4444] text-white" : "bg-white/10 text-white/85 hover:bg-white/20 border border-white/20"}`}>
            {muted ? <MicOff size={13} /> : <Mic size={13} />}
          </button>
        </div>
        <p className="text-[10px] mt-1 font-bold h-3.5 leading-[14px] truncate" data-testid="minute-mic-status"
           style={{ color: muted ? "#ef4444" : micLive ? "#8fbf82" : "rgba(255,255,255,.4)" }}>
          {muted ? "Mic muted — tap the mic icon to unmute"
            : micLive ? "Mic live ✓ — the bar moves as you speak"
            : "Speak to test — if the bar stays flat, tap anywhere once to wake the mic"}
        </p>
      </div>

      {/* Desktop: mic artwork + record on the LEFT, big teleprompter on the RIGHT. Mobile stacks. */}
      <div className="md:flex md:items-stretch">
        {/* LEFT — studio artwork + record deck */}
        <div className="md:w-[42%] md:border-r border-white/10 flex flex-col">
          {/* Studio graphic band */}
          <div className="relative shrink-0 md:shrink md:flex-1 w-full aspect-[4/3] md:aspect-auto md:min-h-[150px] overflow-hidden bg-black">
            <img src="/studio-bg-v2.jpg" alt="" aria-hidden className="absolute inset-0 w-full h-full object-cover object-center select-none pointer-events-none" />
            {countdown > 0 && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/55" data-testid="minute-vo-countdown">
                <span key={countdown} className="text-white font-extrabold" style={{ fontSize: "clamp(64px,22vw,120px)", textShadow: "0 4px 24px rgba(0,0,0,.7)", animation: "sms-pop .3s ease" }}>{countdown}</span>
              </div>
            )}
          </div>

          {/* Record deck */}
          <div className="shrink-0 px-3 pt-2.5 pb-3 bg-[#0c0c11] border-t border-white/10">
            <div className="flex flex-col items-center">
              <div className="w-40 h-1.5 rounded-full bg-black/60 border border-white/10 overflow-hidden">
                <div className="h-full rounded-full transition-[width] duration-100 ease-linear" style={{ width: `${playing ? playPct : pct}%`, background: playing ? "linear-gradient(90deg,#8fbf82,#f0c975)" : phase === "recording" ? "linear-gradient(90deg,#f0c975,#ef4444)" : "rgba(240,201,117,.5)" }} data-testid="minute-vo-bar" />
              </div>
              <div className="mt-1 font-mono font-bold text-sm tabular-nums" style={{ color: phase === "recording" ? "#ef4444" : "#f0c975" }} data-testid="minute-vo-timer">
                {(() => { const s = Math.ceil(remaining); return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`; })()} <span className="text-white/40">/ 1:00</span>
              </div>
            </div>

            {hasTake && phase !== "recording" && phase !== "saving" && (
              <div className="mt-2" data-testid="minute-vo-take">
                <button onClick={play} data-testid="minute-vo-play" className="w-full inline-flex items-center justify-center gap-2 rounded-full py-2.5 text-sm font-extrabold bg-[#f0c975] text-black hover:bg-[#e8b74d] active:scale-[.98] transition-all">
                  {playing ? <><Pause size={16} /> Stop preview</> : <><Play size={16} /> Preview my take</>}
                </button>
                <div className="flex items-center justify-center gap-2 mt-1.5">
                  <span className="inline-flex items-center gap-1 text-[11px] font-bold text-[#8fbf82]">✓ Take saved</span>
                  <button onClick={removeTake} data-testid="minute-vo-delete" className="inline-flex items-center gap-1 text-[11px] font-bold text-[#f7a0b4] hover:text-[#ff8ea0] transition-colors">
                    <Trash2 size={13} /> Delete &amp; re-record
                  </button>
                </div>
              </div>
            )}

            <div className="flex items-start justify-center gap-3 mt-2">
              <button
                onClick={() => genVoice("male")}
                disabled={!!ttsBusy || phase === "recording" || phase === "saving" || countdown > 0}
                data-testid="minute-vo-male"
                title="Generate a male AI voice-over from your script"
                className="flex flex-col items-center gap-1 pt-1 disabled:opacity-40 group"
              >
                <span className="w-[54px] h-[54px] rounded-full flex items-center justify-center border-2 border-[#5d8a52] bg-[#0e1a0b] text-[#a9d79b] group-hover:bg-[#14260f] group-active:scale-95 transition-all">
                  {ttsBusy === "male" ? <Loader2 size={18} className="animate-spin" /> : <Sparkles size={18} />}
                </span>
                <span className="text-[11px] font-extrabold text-white">Male VO</span>
              </button>

              <div className="flex flex-col items-center gap-1">
                <button
                  onClick={phase === "recording" ? stopRec : beginRecord}
                  disabled={phase === "saving" || countdown > 0 || !!ttsBusy}
                  data-testid={phase === "recording" ? "minute-vo-stop" : "minute-vo-record"}
                  className="relative w-[70px] h-[70px] rounded-full flex items-center justify-center transition-transform active:scale-95 disabled:opacity-70"
                  style={{ border: "3px solid rgba(255,255,255,.92)", boxShadow: phase === "recording" ? "0 0 26px 3px rgba(225,29,47,.6)" : "0 4px 16px rgba(0,0,0,.5)" }}>
                  <span className="flex items-center justify-center text-white transition-all duration-200"
                    style={phase === "recording" ? { width: 28, height: 28, borderRadius: 8, background: "#e11d2f" } : { width: 54, height: 54, borderRadius: 999, background: "linear-gradient(135deg,#f0505f,#e11d2f)" }}>
                    {phase === "saving" ? <Loader2 size={20} className="animate-spin" /> : phase === "recording" ? null : hasTake ? <RotateCcw size={22} /> : <Mic size={22} />}
                  </span>
                </button>
                <span className="text-[11px] font-bold text-white/90">{countdown > 0 ? "Get ready…" : phase === "recording" ? "Stop" : phase === "saving" ? "Saving…" : hasTake ? "Re-record" : "Record"}</span>
              </div>

              <button
                onClick={() => genVoice("female")}
                disabled={!!ttsBusy || phase === "recording" || phase === "saving" || countdown > 0}
                data-testid="minute-vo-female"
                title="Generate a female AI voice-over from your script"
                className="flex flex-col items-center gap-1 pt-1 disabled:opacity-40 group"
              >
                <span className="w-[54px] h-[54px] rounded-full flex items-center justify-center border-2 border-[#5d8a52] bg-[#0e1a0b] text-[#a9d79b] group-hover:bg-[#14260f] group-active:scale-95 transition-all">
                  {ttsBusy === "female" ? <Loader2 size={18} className="animate-spin" /> : <Sparkles size={18} />}
                </span>
                <span className="text-[11px] font-extrabold text-white">Female VO</span>
              </button>
            </div>
            <p className="text-center text-[10px] text-white/40 mt-1.5">AI voices read your script — write or Gen one above first</p>
          </div>
        </div>

        {/* RIGHT — Teleprompter hub: AI drafting + a tall, scroll-free script window */}
        <div className="md:flex-1 flex flex-col px-3 pt-3 pb-3 bg-[#0c0c11] border-t md:border-t-0 border-white/10">
          <div className="flex items-center gap-2 mb-2">
            <input
              value={aiPrompt}
              onChange={(e) => setAiPrompt(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); genScript(); } }}
              placeholder={useDomain ? "Your request… e.g. 'Explain what Zeely is — marketing, video, images, ease of use'" : "Prompt a quick script with AI… e.g. '3 tips for first-time campers'"}
              data-testid="minute-script-ai-input"
              className="flex-1 min-w-0 rounded-lg bg-black border-2 border-[#2a3a26] focus:border-[#5d8a52] outline-none text-[13px] text-white placeholder:text-white/30 px-2.5 py-2"
            />
            <button
              onClick={genScript}
              disabled={aiBusy}
              data-testid="minute-script-gen"
              className="shrink-0 inline-flex items-center gap-1.5 rounded-full bg-[#1e3aaa] hover:brightness-110 text-white font-extrabold text-[12px] px-3.5 py-2 disabled:opacity-60 transition-all"
            >
              {aiBusy ? <Loader2 size={14} className="spin" /> : <Sparkles size={14} />} Gen
            </button>
          </div>

          {/* Use my website — pull the script straight from a real business site's language */}
          <div className="mb-2">
            <label data-testid="minute-use-domain-toggle" className="inline-flex items-center gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={useDomain}
                onChange={(e) => setUseDomain(e.target.checked)}
                data-testid="minute-use-domain-checkbox"
                className="w-4 h-4 accent-[#1e3aaa] shrink-0"
              />
              <span className="inline-flex items-center gap-1.5 text-[12px] font-bold text-[#8fbf82]"><Globe size={13} /> Add my website</span>
              <span className="text-[10px] text-white/40 font-bold">layers the site's photos, copy &amp; theme onto your request</span>
            </label>
            {useDomain && (
              <input
                value={domain}
                onChange={(e) => setDomain(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); genScript(); } }}
                placeholder="yourbusiness.com"
                data-testid="minute-domain-input"
                className="mt-1.5 w-full rounded-lg bg-black border-2 border-[#2a3a26] focus:border-[#5d8a52] outline-none text-[13px] text-white placeholder:text-white/30 px-2.5 py-2"
              />
            )}
          </div>

          <div className="flex items-center justify-between mb-1.5">
            <span className="flex items-center gap-1.5 text-[12px] font-bold text-[#f0c975]"><ScrollText size={14} /> Teleprompter</span>
            {locked ? (
              <button onClick={() => setLocked(false)} data-testid="minute-script-edit" className="inline-flex items-center gap-1 text-[11px] font-bold text-white/70 hover:text-[#f0c975] transition-colors"><Pencil size={12} /> Edit</button>
            ) : (
              <button onClick={lockScript} data-testid="minute-script-lock" className="inline-flex items-center gap-1 text-[11px] font-bold text-black bg-[#f0c975] hover:bg-[#e8b74d] rounded-full px-2.5 py-1 transition-colors"><Lock size={11} /> Lock in</button>
            )}
          </div>
          {locked ? (
            <p className="text-[16px] leading-relaxed text-white/95 font-semibold flex-1 max-h-44 md:max-h-none overflow-auto pr-1" data-testid="minute-script-display">
              {script || "Say whatever you'd like — no script needed."}
            </p>
          ) : (
            <textarea
              value={script}
              onChange={(e) => setScript(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); lockScript(); } }}
              placeholder="Type or paste your script (or hit Gen above), then press Enter to lock it in for easy reading…"
              rows={5}
              data-testid="minute-script-input"
              className="w-full flex-1 md:min-h-[300px] rounded-lg bg-black border-2 border-[#2a3a26] focus:border-[#5d8a52] outline-none text-[14px] text-[#a9d79b] p-2.5 resize-none"
            />
          )}
        </div>
      </div>
    </div>
  );
}
